import { Modal } from "antd";

/**
 * Android WebView bridge utilities.
 */

/**
 * If the AndroidOverlay bridge is available and neither the patient's first nor last
 * name is contained (case insensitive) in the extracted patient identifier,
 * shows a warning modal. On "Proceed", runs onProceed; on "Cancel", does nothing.
 * If the bridge is missing (older app), runs onProceed immediately (backwards compatible).
 *
 * @param patientName Full patient name (e.g. "John Doe" or "Patient 6")
 * @param onProceed Callback to run when user proceeds (or when no warning needed)
 */
export function maybeWarnPatientNameMismatch(
  patientName: string,
  onProceed: () => void
): void {
  let extracted: string;
  try {
    const bridge =
      typeof window !== "undefined" ? window.AndroidOverlay : undefined;
    const getMatched = bridge?.getMatchedPatientIdentifier;
    if (!bridge || !getMatched || typeof getMatched !== "function") {
      onProceed();
      return;
    }
    // Must call on bridge object to preserve 'this'. The Android WebView Java bridge
    // requires the correct receiver for injection lookup. Calling the extracted getMatched function variable
    // loses the binding and throws "Java bridge method can't be invoked on a non-injected object":
    //   const fn = bridge.getMatchedPatientIdentifier;
    //   fn();  // BAD - throws
    const result = bridge.getMatchedPatientIdentifier?.();
    if (!result || typeof result !== "string") {
      onProceed();
      return;
    }
    extracted = result;
  } catch {
    onProceed();
    return;
  }
  const normalizedPatientName = (patientName || "").trim();
  if (!normalizedPatientName) {
    onProceed();
    return;
  }

  const parts = normalizedPatientName.split(/\s+/);
  const firstName = parts[0] ?? "";
  const lastName = parts.slice(1).join(" ") ?? "";
  const extractedLower = extracted.toLowerCase();
  const firstNameContained =
    firstName !== "" && extractedLower.includes(firstName.toLowerCase());
  const lastNameContained =
    lastName !== "" && extractedLower.includes(lastName.toLowerCase());
  if (firstNameContained || lastNameContained) {
    onProceed();
    return;
  }
  Modal.confirm({
    title: "Warning!",
    content:
      "Patient name doesn't match. Are you sure you have the right patient?",
    okText: "Proceed",
    cancelText: "Cancel",
    onOk: onProceed,
  });
}

/**
 * Returns the AndroidAudio bridge version.
 * - 0: Not in Android WebView (window.AndroidAudio missing)
 * - 1: Old Android (bridge exists but version() does not)
 * - 2: New Android (stub with version() returning 2)
 */
export function getAndroidAudioVersion(): number {
  try {
    if (typeof window === "undefined" || !window.AndroidAudio) {
      return 0;
    }
    if (typeof window.AndroidAudio.version !== "function") {
      return 1;
    }
    const v = window.AndroidAudio.version();
    return typeof v === "number" ? v : 1;
  } catch {
    return 1;
  }
}
